import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {type DeliveryDate, shoppingCartStorage} from 'dummen-shared';
import type {ShoppingCartOrderLine} from 'dummen-shared/src/interfaces/order.line.type.ts';

export const useShoppingCartStore = defineStore('shoppingCartStore', () => {

	const ORDER_WEEK_THRESHOLD = 2;
	const ORDER_LINE_THRESHOLD = 250;

	/**
	 * The currently active week in the shopping cart, formatted as "week-year".
	 */
	const activeWeek = ref<string | null>(null);

	/**
	 * Sorts a Map of order lines by their week and year in ascending order.
	 * @param orderLinesByWeek The Map to sort
	 */
	const orderLinesSortedByWeek = (orderLinesByWeek: Map<string, ShoppingCartOrderLine[]>) => {
		return new Map([...orderLinesByWeek.entries()].sort((a, b) => {
			const [weekA, yearA] = a[0].split('-').map(Number);
			const [weekB, yearB] = b[0].split('-').map(Number);

			return (yearA === yearB) ? (weekA - weekB) : (yearA - yearB);
		}));
	}

	/**
	 * Groups order lines by their delivery week and year.
	 * The key is a string in the format "week-year".
	 */
	const orderLinesPerWeek = computed((): Map<string, ShoppingCartOrderLine[]> => {
		if (!shoppingCartStorage.value.orderLines?.length) {
			return new Map();
		}

		const orderLinesByWeek = Map.groupBy(
			shoppingCartStorage.value.orderLines,
			(orderLine) => `${orderLine.week}-${orderLine.year}`
		);

		return orderLinesSortedByWeek(orderLinesByWeek);
	});

	const getOrderLinesForWeek = (week: number, year: number) => orderLinesPerWeek.value.get(`${week}-${year}`) ?? [];

	/**
	 * Determine if the weeks should be collapsed based on the number of weeks and total amount of order lines.
	 */
	const collapseWeeks = computed(() => {
		if (orderLinesPerWeek.value?.size < ORDER_WEEK_THRESHOLD) {
			return false;
		}

		return shoppingCartStorage.value.orderLines.length >= ORDER_LINE_THRESHOLD;
	});

	/**
	 * Set the active week based on the provided delivery date and scrolls to the active week element.
	 * @param deliveryDate The delivery date containing week and year information.
	 */
	const setActiveWeek = (deliveryDate: DeliveryDate | null) => {
		activeWeek.value = deliveryDate ? `${deliveryDate.week}-${deliveryDate.year}` : null
	}

	const setInitialActiveWeek = () => {
		if (!activeWeek.value && collapseWeeks.value) {
			activeWeek.value = orderLinesPerWeek.value.keys().next().value ?? null;
		}
	}

	return {
		activeWeek,
		collapseWeeks,
		orderLinesPerWeek,
		getOrderLinesForWeek,
		setActiveWeek,
		setInitialActiveWeek,
	};
});
